'use client';

import type { LexicalContent } from '@mv/core/model/heim/richText';
import {
	type RichTextRenderers,
	RichText as SharedRichText,
	type RichTextProps as SharedRichTextProps,
} from '@mv/rich-text-renderer';
import type { HTMLAttributes, RefAttributes } from 'react';
import { useI18n } from '@/modules/i18n/useI18n';
import Link, { type Link as RichTextLinkType } from '@/payload/components/Link/RichTextLink';
import type { NodeTypes } from './serialize';

export type LexicalRoot = {
	type: string;
	children: NodeTypes[];
	direction: 'ltr' | 'rtl' | null;
	format: 'left' | 'start' | 'center' | 'right' | 'end' | 'justify' | '';
	indent: number;
	version: number;
};

export type RichTextOptions = {
	size?: 'small' | 'regular';
};

export type RichTextContent = {
	root?: LexicalRoot;
	[k: string]: unknown | undefined;
};

export type RichTextProps = Omit<HTMLAttributes<HTMLSpanElement>, 'content'> &
	RefAttributes<HTMLSpanElement> & {
		content: RichTextContent | LexicalContent | null;
		className?: string;
		options?: RichTextOptions;
		pageLastPublishedOn?: string;
	};

export default function RichText({ content, className, options, pageLastPublishedOn, ...restProps }: RichTextProps) {
	const { locale } = useI18n();
	if (!content?.root) return null;

	const replacements = pageLastPublishedOn ? { pageLastUpdatedOn: pageLastPublishedOn } : {};
	const resolveLink = ({ href, linkType, slug }: Parameters<NonNullable<SharedRichTextProps['resolveLink']>>[0]) => ({
		href:
			linkType === 'internal' && slug ? (slug === 'home' ? `/${locale}/support` : `/${locale}/support/${slug}`) : href,
	});
	const renderers: Pick<RichTextRenderers, 'link'> = {
		link: (node, children, key) => (
			<Link key={key} link={node.fields as RichTextLinkType}>
				{children}
			</Link>
		),
	};

	return (
		<SharedRichText
			content={content as SharedRichTextProps['content']}
			className={className}
			size={options?.size ?? 'regular'}
			replacements={replacements}
			resolveLink={resolveLink}
			renderers={renderers}
			rootProps={{ ...restProps } as SharedRichTextProps['rootProps']}
		/>
	);
}
